/**
 * Quads without instancing: per-quad records are replicated onto 4 corner vertices and
 * drawn as indexed triangles. (Software GL — SwiftShader, Chrome's fallback on
 * blocklisted GPUs — runs every instance as a separate draw; this keeps it to one.)
 * Shaders read the record attributes plus `aCorner` (x ∈ {0,1}, y ∈ {−1,1}).
 */
import type { GL } from './gl';

const CORNERS = [0, -1, 1, -1, 0, 1, 1, 1];

/** Expand `n` records of `stride` floats into 4 vertices of `stride + 2` floats each. */
export function expandQuads(src: Float32Array, stride: number, n: number, out?: Float32Array): Float32Array {
  const vs = stride + 2;
  const need = n * 4 * vs;
  const dst = out && out.length >= need ? out : new Float32Array(need);
  for (let i = 0; i < n; i++) {
    const rec = src.subarray(i * stride, (i + 1) * stride);
    for (let c = 0; c < 4; c++) {
      const o = (i * 4 + c) * vs;
      dst.set(rec, o);
      dst[o + stride] = CORNERS[c * 2];
      dst[o + stride + 1] = CORNERS[c * 2 + 1];
    }
  }
  return dst;
}

/** Shared index buffer for quads: 0 1 2, 2 1 3 per quad, grown on demand. */
export class QuadIndices {
  readonly buf: WebGLBuffer;
  private cap = 0;

  constructor(private gl: GL) {
    this.buf = gl.createBuffer()!;
  }

  /** Make room for n quads. Binds the buffer to ELEMENT_ARRAY_BUFFER (bind your VAO first). */
  ensure(n: number): void {
    const gl = this.gl;
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.buf);
    if (n <= this.cap) return;
    let cap = Math.max(1024, this.cap);
    while (cap < n) cap *= 2;
    const idx = new Uint32Array(cap * 6);
    for (let i = 0; i < cap; i++) idx.set([i * 4, i * 4 + 1, i * 4 + 2, i * 4 + 2, i * 4 + 1, i * 4 + 3], i * 6);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, idx, gl.STATIC_DRAW);
    this.cap = cap;
  }
}

/** VAO reading `attrs` (vec4 each, in record order) + `aCorner` from an expanded buffer. */
export function quadVao(gl: GL, prog: WebGLProgram, buf: WebGLBuffer, stride: number, attrs: string[], idx: QuadIndices): WebGLVertexArrayObject {
  const vao = gl.createVertexArray()!;
  const bytes = (stride + 2) * 4;
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  attrs.forEach((name, i) => {
    const loc = gl.getAttribLocation(prog, name);
    if (loc < 0) return;
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 4, gl.FLOAT, false, bytes, i * 16);
  });
  const lc = gl.getAttribLocation(prog, 'aCorner');
  gl.enableVertexAttribArray(lc);
  gl.vertexAttribPointer(lc, 2, gl.FLOAT, false, bytes, stride * 4);
  idx.ensure(1);
  gl.bindVertexArray(null);
  return vao;
}
